import { createContext, type ReactNode, useContext } from "react"
import { CheckCircle2, CircleAlert } from "lucide-react"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"

interface AlertData {
  error: boolean
  text: string | ReactNode
}

interface AlertContextType {
  showAlert: (alert: AlertData) => void
}

const AlertContext = createContext<AlertContextType | undefined>(undefined)

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  // No shared toast id: sonner defers dismiss(id) to the next animation frame, so a
  // dismiss-then-create pair on one fixed id kills the new toast. visibleToasts={1}
  // already guarantees only the latest toast is shown.
  const showAlert = (alertData: AlertData) => {
    if (alertData.error) {
      toast.error(alertData.text, {
        closeButton: true,
        duration: 3000,
      })
      return
    }

    toast.success(alertData.text, {
      closeButton: true,
      duration: 3000,
    })
  }

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <Toaster
        closeButton
        expand={false}
        icons={{
          error: <CircleAlert className="size-4" />,
          success: <CheckCircle2 className="size-4" />,
        }}
        offset={16}
        position="bottom-left"
        richColors
        toastOptions={{
          classNames: {
            closeButton:
              "border-0 bg-transparent text-muted-foreground opacity-100 hover:bg-muted hover:text-foreground",
            description: "text-sm font-medium leading-6",
            toast:
              "w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-background text-foreground shadow-xl",
          },
        }}
        visibleToasts={1}
      />
    </AlertContext.Provider>
  )
}

export const useAlert = () => {
  const context = useContext(AlertContext)
  if (!context) throw new Error("useAlert must be used within an AlertProvider")

  return context
}
